import type { NavigateFunction } from 'react-router-dom';
import type { LoginResponse } from '../../../modules/platform/types';
import type { User } from '../../../modules/platform/types';
import { getBanyanproEmbedEnabled, getEmbedAssertionTimeoutMs } from '../config/embedConfig';
import {
  LMS_EMBED_DISABLED,
  LMS_EMBED_TIMEOUT,
  LMS_EMBED_MALFORMED_MESSAGE,
  getEmbedErrorPresentation,
} from '../errors/embedErrors';
import {
  attachParentAssertionListener,
  postEmbedReadyToParent,
  postLaunchErrorToParent,
  postLaunchSuccessToParent,
} from '../postmessage/handlers';
import { runLmsLaunchFlow } from './launchFlow';
import type { LmsLaunchErrorPresentation } from '../../../utils/lmsLaunchErrors';

export type LaunchControllerCallbacks = {
  onWaiting: () => void;
  onLaunching: () => void;
  onError: (presentation: LmsLaunchErrorPresentation) => void;
  onSuccess: (targetPath: string) => void;
};

export type LaunchControllerDeps = {
  completeLmsSession: (response: LoginResponse) => Promise<User>;
  navigate: NavigateFunction;
  returnUrl?: string | null;
};

/**
 * postMessage embed launch: READY → assertion → POST /auth/lms/launch → session → notify parent.
 */
export function startPostMessageLaunch(
  deps: LaunchControllerDeps,
  callbacks: LaunchControllerCallbacks
): () => void {
  if (!getBanyanproEmbedEnabled()) {
    const presentation = getEmbedErrorPresentation(LMS_EMBED_DISABLED);
    callbacks.onError(presentation);
    postLaunchErrorToParent(presentation.code ?? LMS_EMBED_DISABLED, presentation.message);
    return () => undefined;
  }

  callbacks.onWaiting();

  let settled = false;
  let timeoutId: ReturnType<typeof setTimeout> | undefined;

  const finish = () => {
    if (timeoutId !== undefined) {
      clearTimeout(timeoutId);
      timeoutId = undefined;
    }
  };

  const runLaunch = async (assertionJwt: string) => {
    if (settled) {
      return;
    }
    settled = true;
    finish();
    callbacks.onLaunching();
    const result = await runLmsLaunchFlow(assertionJwt, {
      returnUrl: deps.returnUrl,
      completeLmsSession: deps.completeLmsSession,
    });
    if (result.ok) {
      postLaunchSuccessToParent(result.targetPath);
      callbacks.onSuccess(result.targetPath);
      deps.navigate(result.targetPath, { replace: true });
    } else {
      const code = result.presentation.code ?? 'LMS_LAUNCH_FAILED';
      postLaunchErrorToParent(code, result.presentation.message);
      callbacks.onError(result.presentation);
    }
  };

  const detach = attachParentAssertionListener(
    (msg) => {
      void runLaunch(msg.payload.assertion_jwt);
    },
    undefined,
    () => {
      if (settled) {
        return;
      }
      settled = true;
      finish();
      const presentation = getEmbedErrorPresentation(LMS_EMBED_MALFORMED_MESSAGE);
      postLaunchErrorToParent(LMS_EMBED_MALFORMED_MESSAGE, presentation.message);
      callbacks.onError(presentation);
    }
  );

  postEmbedReadyToParent();

  timeoutId = setTimeout(() => {
    if (settled) {
      return;
    }
    settled = true;
    finish();
    detach();
    const presentation = getEmbedErrorPresentation(LMS_EMBED_TIMEOUT);
    postLaunchErrorToParent(LMS_EMBED_TIMEOUT, presentation.message);
    callbacks.onError(presentation);
  }, getEmbedAssertionTimeoutMs());

  return () => {
    settled = true;
    finish();
    detach();
  };
}
